import dayjs from "dayjs";
import { useForm } from "react-hook-form";

import { Button } from "@components/Button";
import { ErrorComponent } from "@components/ErrorComponent";
import { SelectionInput, TextInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { DEFAULT_COUNTRY, VERIFIED_TERMS_OF_USE_URL } from "@constants";
import { DateFormat, State, UserResidenceStatus } from "@ctypes";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCountries,
  useErrorBanner,
  useOnboardingSavePoint,
  usePrefill,
  useSetUserIdentity,
  useUser,
  useValidateUserPrefill,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { formatPhoneNumber, formatSsnNumber } from "@utils";
import { CSSProperties, useState } from "react";
import { validationSchema } from "./content";
import { Divider, Footer, ItemContainer, Row, Separator, useStyles } from "./styles";
import { VerifiedFormValues } from "./types";

const options = [
  i18n.t(Translations.CitizenshipRouteResidenceStatusCitizen),
  i18n.t(Translations.CitizenshipRouteResidenceStatusResident),
  i18n.t(Translations.CitizenshipRouteResidenceStatusNonResident),
  i18n.t(Translations.CitizenshipRouteResidenceStatusNone),
];

export const VerifiedRoute = () => {
  const { loading: userLoading, error: userError } = useUser();
  const { loading: countriesLoading, error: countriesError } = useCountries();

  // Summary first (mobile parity); the pencil icons open the full edit form.
  const [editing, setEditing] = useState(false);

  const loading = userLoading || countriesLoading;
  const error = userError || countriesError;

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  if (!editing) {
    return <VerifiedSummary onEdit={() => setEditing(true)} />;
  }

  return <VerifiedRouteComponent />;
};

/** Masked DOB row, mobile-style: 08-01-****. */
const maskDateOfBirth = (dateOfBirth?: string | null) =>
  dateOfBirth ? `${dayjs(dateOfBirth).format("MM-DD")}-****` : "";

const PencilIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#E09B7C"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z" />
  </svg>
);

/**
 * Mobile ReviewProfileScreen parity: read-only rows from the Verified
 * prefill with per-row edit affordances, a provenance banner, and a
 * Continue that submits the prefill as-is. When the SSN wasn't returned,
 * identity is saved and the machine collects just the SSN afterwards.
 */
const VerifiedSummary = ({ onEdit }: { onEdit: () => void }) => {
  const [submitting, setSubmitting] = useState(false);

  const { showErrorBanner } = useErrorBanner();
  const { user } = useUser();
  const { prefillProps: prefill, clearPrefillProps } = usePrefill();

  // validateUserPrefill returns a scalar, so the cached user stays stale;
  // the savepoint refetches before the onboarding machine decides the next
  // screen – otherwise it re-asks for the DOB and address Verified just set.
  const [navigateToNextRoute] = useOnboardingSavePoint();

  const [validateUserPrefill] = useValidateUserPrefill();
  const [setUserIdentity] = useSetUserIdentity();

  const fullName = [prefill.firstName, prefill.lastName].filter(Boolean).join(" ");
  const addressLine = [prefill.address, prefill.city, prefill.state, prefill.zipCode]
    .filter(Boolean)
    .join(", ");

  const handleContinue = async () => {
    try {
      setSubmitting(true);
      if (prefill.ssn) {
        await validateUserPrefill({
          variables: {
            firstName: prefill.firstName ?? "",
            lastName: prefill.lastName ?? "",
            email: prefill.email ?? user?.email ?? "",
            phoneNumber: (prefill.phoneNumber ?? user?.phoneNumber ?? "").replace(/\D/g, ""),
            dateOfBirth: dayjs(prefill.dateOfBirth).format(DateFormat.User),
            address: {
              street: prefill.address ?? "",
              city: prefill.city ?? "",
              postalCode: prefill.zipCode ?? "",
              state: prefill.state ?? "",
              unitNo: prefill.address2 ?? "",
              country: DEFAULT_COUNTRY,
            },
            ssn: prefill.ssn,
          },
        });
      } else {
        // No SSN from Verified: persist the confirmed identity and let the
        // onboarding machine ask for the SSN alone.
        await setUserIdentity({
          variables: {
            firstName: prefill.firstName,
            lastName: prefill.lastName,
            dateOfBirth: dayjs(prefill.dateOfBirth).format(DateFormat.User),
            address: {
              street: prefill.address ?? "",
              city: prefill.city ?? "",
              postalCode: prefill.zipCode ?? "",
              state: prefill.state ?? "",
              unitNo: prefill.address2 ?? "",
              country: DEFAULT_COUNTRY,
            },
          },
        });
      }

      await setUserIdentity({
        variables: {
          countryOfCitizenship: DEFAULT_COUNTRY,
          residenceStatus: UserResidenceStatus.Citizen,
        },
      });

      clearPrefillProps();
      await navigateToNextRoute();
    } catch {
      showErrorBanner();
    } finally {
      setSubmitting(false);
    }
  };

  const rows: Array<{ label: string; value: string }> = [
    {
      label: i18n.t(Translations.VerifiedSummaryFullName),
      value: fullName,
    },
    {
      label: i18n.t(Translations.VerifiedSummaryDateOfBirth),
      value: maskDateOfBirth(prefill.dateOfBirth),
    },
    {
      label: i18n.t(Translations.VerifiedSummaryAddress),
      value: addressLine,
    },
    {
      label: i18n.t(Translations.VerifiedSummaryCitizenship),
      value: DEFAULT_COUNTRY,
    },
  ];

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.VerifiedSummaryTitle),
    subtitle: i18n.t(Translations.VerifiedSummarySubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.VerifiedSummaryPrimaryCTA),
        onClick: handleContinue,
        loading: submitting,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <div style={summaryStyles.card}>
        {rows.map((row) => (
          <div key={row.label} style={summaryStyles.row}>
            <div style={summaryStyles.label}>{row.label}</div>
            <button
              type="button"
              style={summaryStyles.valueButton}
              onClick={onEdit}
              aria-label={`Edit ${row.label}`}
            >
              <span style={summaryStyles.value}>{row.value}</span>
              <PencilIcon />
            </button>
          </div>
        ))}
      </div>

      <Spacing size={SpacingSizes.M} />

      <a
        href={VERIFIED_TERMS_OF_USE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={summaryStyles.banner}
      >
        <span style={summaryStyles.bannerText}>
          {i18n.t(Translations.VerifiedSummarySecureMessage)}
        </span>
        <span style={summaryStyles.bannerInfo}>i</span>
      </a>
    </OnboardingLayout>
  );
};

const summaryStyles: Record<string, CSSProperties> = {
  card: {
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: 16,
    background: "rgba(244,241,242,0.06)",
    backdropFilter: "blur(12px)",
    padding: "6px 20px",
  },
  row: {
    padding: "16px 0",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  label: {
    fontSize: 13,
    color: "rgba(255,255,255,0.55)",
    marginBottom: 5,
  },
  valueButton: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
  },
  value: {
    fontSize: 17,
    fontWeight: 500,
    color: "#F2EFE9",
    lineHeight: 1.4,
  },
  banner: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "16px 18px",
    borderRadius: 14,
    background: "rgba(205,128,84,0.18)",
    border: "1px solid rgba(205,128,84,0.32)",
    textDecoration: "none",
  },
  bannerText: {
    flex: 1,
    fontSize: 14,
    color: "rgba(255,243,224,0.92)",
  },
  bannerInfo: {
    width: 22,
    height: 22,
    borderRadius: 11,
    border: "1px solid rgba(255,243,224,0.6)",
    color: "rgba(255,243,224,0.85)",
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
};

export const VerifiedRouteComponent = () => {
  const [isUpdating, setIsUpdating] = useState(false);

  const styles = useStyles();

  const { showErrorBanner } = useErrorBanner();

  const { user } = useUser();
  const { data: countries } = useCountries();

  const { prefillProps: prefill, clearPrefillProps } = usePrefill();
  const { intake } = useEntryIntakeStore.getState();

  const isVerifiedPrefillEdit = Boolean(prefill.ssn);
  const showSsnField = !isVerifiedPrefillEdit;

  const countryValues = (() => {
    if (!countries?.countries) return [];

    return Object.values(countries.countries);
  })() as Array<string>;

  const countryKeys = (() => {
    if (!countries?.countries) return [];

    return Object.keys(countries.countries);
  })() as Array<string>;

  const defaultIndex = (() => {
    return Object.values(State).findIndex((state) => state === prefill.state);
  })();

  const defaultCountryIndex = countryValues.findIndex((c) => c === DEFAULT_COUNTRY);

  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  const [countryIndex, setCountryIndex] = useState(defaultCountryIndex);
  const [residencyStatusIndex, setResidencyStatusIndex] = useState(0);

  const [setUserIdentity] = useSetUserIdentity();

  const residenceStatusType = (() => {
    const value = options[residencyStatusIndex];

    switch (value) {
      case i18n.t(Translations.CitizenshipRouteResidenceStatusCitizen):
        return UserResidenceStatus.Citizen;
      case i18n.t(Translations.CitizenshipRouteResidenceStatusResident):
        return UserResidenceStatus.Resident;
      case i18n.t(Translations.CitizenshipRouteResidenceStatusNonResident):
        return UserResidenceStatus.NonResident;
      case i18n.t(Translations.CitizenshipRouteResidenceStatusNone):
        return UserResidenceStatus.None;
    }
  })();

  const [validateUserPrefill] = useValidateUserPrefill();

  const [navigateToNextRoute] = useOnboardingSavePoint();

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid: isFormValid },
  } = useForm<VerifiedFormValues>({
    defaultValues: {
      firstName: prefill.firstName ?? user?.firstName ?? intake.firstName ?? "",
      lastName: prefill.lastName ?? user?.lastName ?? intake.lastName ?? "",
      address: prefill.address ?? user?.userAddress?.street ?? "",
      address2: prefill.address2 ?? user?.userAddress?.unitNo ?? "",
      city: prefill.city ?? user?.userAddress?.city ?? "",
      state: prefill.state ?? (user?.userAddress?.state as State) ?? State.Alabama,
      zipCode: prefill.zipCode ?? user?.userAddress?.postalCode ?? "",
      ssn: "",
    },
    mode: "all",
    // @ts-expect-error: FIXME
    resolver: yupResolver(validationSchema),
  });

  const phoneNumber = formatPhoneNumber(prefill.phoneNumber ?? user?.phoneNumber ?? "");
  const email = prefill.email ?? user?.email ?? "";

  const isResidenceValid =
    residenceStatusType === UserResidenceStatus.Citizen ||
    residenceStatusType === UserResidenceStatus.Resident;
  const isValid = isFormValid && isResidenceValid;

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.VerifiedRouteTitle),
  };

  const firstNameController = register("firstName");
  const lastNameController = register("lastName");
  const addressController = register("address");
  const unitNoController = register("address2");
  const cityController = register("city");
  const stateController = register("state");
  const zipCodeController = register("zipCode");
  const ssnController = register("ssn");

  const onChangeSsn = (value: string) => {
    const formattedValue = formatSsnNumber(value);

    setValue("ssn", formattedValue, constants.form);
  };

  const handleSubmit = async (values: VerifiedFormValues) => {
    try {
      setIsUpdating(true);

      await validateUserPrefill({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          email,
          phoneNumber: phoneNumber.replace(/\D/g, ""),
          dateOfBirth: dayjs(user?.dateOfBirth ?? prefill.dateOfBirth).format(DateFormat.User),
          address: {
            street: values.address,
            city: values.city,
            postalCode: values.zipCode,
            state: values.state,
            unitNo: values.address2,
            country: DEFAULT_COUNTRY,
          },
          ssn: values.ssn ? values.ssn : (prefill.ssn ?? ""),
        },
      });

      await setUserIdentity({
        variables: {
          countryOfCitizenship: countryKeys[countryIndex],
          residenceStatus: residenceStatusType,
        },
      });

      clearPrefillProps();
      await navigateToNextRoute();
    } catch {
      showErrorBanner();
    } finally {
      setIsUpdating(false);
    }
  };

  if (!countryKeys.length) return <ErrorComponent />;

  return (
    <OnboardingLayout {...props} style={styles.container}>
      <Row>
        <ItemContainer>
          <TextInput
            {...firstNameController}
            label={i18n.t(Translations.VerifiedRouteFirstNameLabel)}
            value={watch("firstName")}
            onChangeText={(text) => setValue("firstName", text, constants.form)}
            error={errors.firstName?.message}
          />
        </ItemContainer>
        <Divider />
        <ItemContainer>
          <TextInput
            {...lastNameController}
            label={i18n.t(Translations.VerifiedRouteLastNameLabel)}
            value={watch("lastName")}
            onChangeText={(text) => setValue("lastName", text, constants.form)}
            error={errors.lastName?.message}
          />
        </ItemContainer>
      </Row>

      <Row>
        <ItemContainer>
          <TextInput
            label={i18n.t(Translations.VerifiedRoutePhoneNumberLabel)}
            value={phoneNumber}
            disabled
          />
        </ItemContainer>
        <Divider />
        <ItemContainer>
          <TextInput label={i18n.t(Translations.VerifiedRouteEmailLabel)} value={email} disabled />
        </ItemContainer>
      </Row>

      <Row>
        <ItemContainer>
          <TextInput
            {...addressController}
            label={i18n.t(Translations.VerifiedRouteAddressLabel)}
            value={watch("address")}
            onChangeText={(text) => setValue("address", text, constants.form)}
            error={errors.address?.message}
          />
        </ItemContainer>
        <Divider />
        <ItemContainer>
          <TextInput
            {...unitNoController}
            label={i18n.t(Translations.VerifiedRouteAddressUnitNoLabel)}
            value={watch("address2")}
            onChangeText={(text) => setValue("address2", text, constants.form)}
            error={errors.address2?.message}
          />
        </ItemContainer>
      </Row>

      <Row>
        <ItemContainer>
          <div style={styles.city}>
            <TextInput
              {...cityController}
              label={i18n.t(Translations.VerifiedRouteCityLabel)}
              value={watch("city")}
              onChangeText={(text) => setValue("city", text, constants.form)}
              error={errors.city?.message}
            />

            <Separator />

            <SelectionInput
              {...stateController}
              label={i18n.t(Translations.VerifiedRouteStateLabel)}
              items={Object.values(State)}
              defaultIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              onChangeSelection={(value) => {
                setValue("state", value, constants.form);
              }}
              style={styles.state}
            />
          </div>
        </ItemContainer>
        <Divider />
        <ItemContainer>
          <TextInput
            {...zipCodeController}
            label={i18n.t(Translations.VerifiedRouteZipCodeLabel)}
            value={watch("zipCode")}
            onChangeText={(text) => setValue("zipCode", text, constants.form)}
            error={errors.zipCode?.message}
          />
        </ItemContainer>
      </Row>

      <Row>
        <ItemContainer>
          <SelectionInput
            label={i18n.t(Translations.CitizenshipRouteCountryOfTaxResidencePrompt)}
            items={countryValues}
            setSelectedIndex={setCountryIndex}
            defaultIndex={defaultCountryIndex}
          />
        </ItemContainer>
        <Divider />

        <ItemContainer>
          <SelectionInput
            label={i18n.t(Translations.CitizenshipRouteResidencyStatusPrompt)}
            items={options}
            setSelectedIndex={setResidencyStatusIndex}
            defaultIndex={0}
          />
        </ItemContainer>
      </Row>

      {!isResidenceValid && (
        <>
          <Body style={styles.residence}>
            {i18n.t(Translations.CitizenshipRouteRestrictionsResident)}
          </Body>
          <Spacing size={SpacingSizes.M} />
        </>
      )}

      {showSsnField && (
        <Row>
          <ItemContainer>
            <TextInput
              {...ssnController}
              placeholder={i18n.t(Translations.SsnRouteInputPlaceholder)}
              label={i18n.t(Translations.VerifiedRouteSSNLabel)}
              value={watch("ssn")}
              onChangeText={onChangeSsn}
              error={errors.ssn?.message}
            />
          </ItemContainer>
        </Row>
      )}
      <Footer>
        <ItemContainer>
          <Button
            title={i18n.t(Translations.VerifiedRoutePrimaryCTA)}
            onClick={handleFormSubmit(handleSubmit)}
            disabled={!isValid}
            loading={isUpdating}
          />
        </ItemContainer>
      </Footer>
    </OnboardingLayout>
  );
};
